// src/handover/DropoffHandoverForm.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui';
import { createAuditedClient } from '../utils/auditClient';
import { getUrl } from 'aws-amplify/storage';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import {
  cardStyle,
  labelStyle,
  inputStyle,
  textareaStyle,
  sectionTitleStyle,
  twoColGrid,
  threeColGrid,
  COLORS,
  helperTextStyle,
} from './handoverStyles';

import {
  uploadFiles,
  dataUrlToFile,
  uploadedItemsToKeys,
  countPendingUploads,
  countFailedUploads,
} from './handoverUtils';
import { getDropoffContractText } from './handoverContracts';
import { SubmitHandoverForm } from './handoverQueries';

import SignaturePad from '../components/handover/SignaturePad';
import FileUploadWithPreview from '../components/handover/FileUploadWithPreview';

const client = createAuditedClient('DropoffHandoverForm');

const UpdateReservationChecklist = /* GraphQL */ `
  mutation UpdateReservation($input: UpdateReservationInput!) {
    updateReservation(input: $input) {
      id
      pickupChecklistStatus
      dropoffChecklistStatus
      updatedAt
    }
  }
`;

const CURRENCY_OPTIONS = ['USD', 'EUR', 'GEL', 'LEK', 'GBP', 'AED'];

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function normCurrency(v, fallback = 'USD') {
  const s = String(v || '').trim().toUpperCase();
  return s || fallback;
}
function companyTzForLocation(location) {
  if (String(location).toUpperCase() === 'AL') return 'Europe/Tirane';
  if (String(location).toUpperCase() === 'GE') return 'Asia/Tbilisi';
  return 'UTC';
}

function splitCompanyDateTime(utcIso, tz) {
  if (!utcIso) return { date: '', time: '' };
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  const z = toZonedTime(d, tz);
  return {
    date: format(z, 'yyyy-MM-dd'),
    time: format(z, 'HH:mm'),
  };
}

// If you later need to compute differences in company tz, keep this:
function utcDateFromCompanyParts(dateYmd, timeHm, tz) {
  if (!dateYmd || !timeHm) return null;
  const s = `${dateYmd}T${timeHm}:00`;
  const d = fromZonedTime(s, tz);
  return Number.isNaN(d.getTime()) ? null : d;
}

function sumByCurrency(items) {
  const out = {};
  for (const it of items || []) {
    if (!it) continue;
    const cur = normCurrency(it.currency, 'USD');
    const amt = safeNum(it.amount);
    if (!amt) continue;
    out[cur] = (out[cur] || 0) + amt;
  }
  return out;
}

function fmt(n) {
  const x = safeNum(n);
  return x ? x.toFixed(2) : '0.00';
}

function dictToSortedPairs(d) {
  const entries = Object.entries(d || {}).filter(([, v]) => Math.abs(safeNum(v)) > 1e-9);
  entries.sort((a, b) => a[0].localeCompare(b[0]));
  return entries;
}

export default function DropoffHandoverForm({
  reservationId,
  reservation,
  cars,
  claims,
  pickupFuelLevel,
  pickupHandover,
  location, // 'GE' | 'AL'
  readOnly = false,
  existingHandover = null,
  pushAlert,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [isMobile, setIsMobile] = useState(false);

  const isAlbania = location === 'AL';
  const defaultCurrency = location === 'AL' ? 'EUR' : 'USD';
const companyTz = useMemo(() => companyTzForLocation(location), [location]);

  const [extrasDamageUploads, setExtrasDamageUploads] = useState([]);
  const [washCarUploads, setWashCarUploads] = useState([]);
  const [fuelOdoUploads, setFuelOdoUploads] = useState([]);
  const [bodyDamageUploads, setBodyDamageUploads] = useState([]);

  const [fuelDeliveredLocked, setFuelDeliveredLocked] = useState(false);

  const [submitStage, setSubmitStage] = useState(null);
  const [submitPct, setSubmitPct] = useState(0);
  const [submitDetail, setSubmitDetail] = useState('');

  function setStage(stage, pct, detail = '') {
    setSubmitStage(stage);
    setSubmitPct(pct);
    setSubmitDetail(detail);
  }

  const pendingPhotoUploads =
    countPendingUploads(extrasDamageUploads) +
    countPendingUploads(washCarUploads) +
    countPendingUploads(fuelOdoUploads) +
    countPendingUploads(bodyDamageUploads);
  const failedPhotoUploads =
    countFailedUploads(extrasDamageUploads) +
    countFailedUploads(washCarUploads) +
    countFailedUploads(fuelOdoUploads) +
    countFailedUploads(bodyDamageUploads);

  const [existingCarPhotoUrls, setExistingCarPhotoUrls] = useState([]);
  const [existingDamagePhotoUrls, setExistingDamagePhotoUrls] = useState([]);

  function showAlert(type, title, message) {
    if (typeof pushAlert === 'function') pushAlert({ type, title, message });
    else alert(message || title || 'Something went wrong');
  }

  const [showContract, setShowContract] = useState(false);
  const contractText = useMemo(() => getDropoffContractText(location), [location]);

  const [form, setForm] = useState({
    // identity
    lesseeFirstName: '',
    lesseeLastName: '',
    lesseeEmail: '',
    lesseePhone: '',
    idNumber: '',
    drivingLicenseNumber: '',
    drivingLicenseCountry: '',

    // return info
    dropoffDate: '',
    dropoffTime: '',
    dropoffLocation: isAlbania ? 'Tirana, Albania' : 'Tbilisi, Georgia',
    dropoffLocationChanged: '',
    newDropoffLocation: '',

    // extras
    extrasReturnedCarSeat: true,
    extrasReturnedBooster: true,
    extrasReturnedSnowChains: true,
    extrasDamage: '',

    // condition sections
    carWashed: 'yes',
    fuelCondition: '',
    fuelDeliveredAmount: '',
    fuelReturnedAmount: '',
    bodySameCondition: '',
    damageDescription: '',

    // CHARGES (each has its own currency)
    charges: {
      delivery: { amount: '', currency: defaultCurrency },
      extrasDamage: { amount: '', currency: defaultCurrency },
      carWash: { amount: '', currency: defaultCurrency },
      fuel: { amount: '', currency: defaultCurrency },
      bodyDamage: { amount: '', currency: defaultCurrency },
      // moved into its own section, but still lives here:
      fines: { amount: '', currency: defaultCurrency },
      extraDays: { days: '', amount: '', currency: defaultCurrency },
    },

    // Deposit received (pickup)
    depositAmount: '',
    depositCurrencyCollected: defaultCurrency,
    depositReceivedMethod: '', // cash | card | bank | other

    // Deposit outcome (drop-off)
    depositOutcome: '', // full_release | partial_release | on_hold
    depositOutcomeReason: '',

    // Deposit allocation is separate from release/hold status.
    depositKeptAmount: '', // amount taken/deducted from deposit (same currency as collected)
    depositOnHoldAmount: '', // remaining deposit still held after any deduction
    depositReleaseMethod: '', // cash | card_refund | hold_release | other
    depositToReturnAmount: '', // auto computed for full/partial release
    depositToReturnCurrency: defaultCurrency,

    // Payments received for charges outside deposit
    // If method === 'deposit' => informational row; no amount/currency.
    chargePayments: [{ method: 'cash', amount: '', currency: defaultCurrency, note: '' }],

    // Signature
    customerRefusedToSign: false,
    lesseeSignatureDataUrl: '',
  });

  const [initializedFromReservation, setInitializedFromReservation] = useState(false);
  const [initializedFromExisting, setInitializedFromExisting] = useState(false);

  // -----------------------------
  // Responsive layout
  // -----------------------------
  useEffect(() => {
    function handleResize() {
      if (typeof window === 'undefined') return;
      setIsMobile(window.innerWidth <= 640);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const stackedGrid = { display: 'grid', gridTemplateColumns: '1fr', gap: 14 };
  const twoColResponsive = isMobile ? stackedGrid : twoColGrid;
  const threeColResponsive = isMobile ? stackedGrid : threeColGrid;

  // -----------------------------
  // Helpers
  // -----------------------------
  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function updateCharge(key, patch) {
    setForm((prev) => ({
      ...prev,
      charges: {
        ...prev.charges,
        [key]: { ...(prev.charges?.[key] || {}), ...patch },
      },
    }));
  }

  function updateChargeExtraDays(patch) {
    setForm((prev) => ({
      ...prev,
      charges: {
        ...prev.charges,
        extraDays: { ...(prev.charges?.extraDays || {}), ...patch },
      },
    }));
  }

  function updatePaymentRow(idx, patch) {
    setForm((prev) => {
      const rows = Array.isArray(prev.chargePayments) ? [...prev.chargePayments] : [];
      rows[idx] = { ...(rows[idx] || {}), ...patch };

      // If switching to deposit method, wipe amount/currency to prevent double-entry.
      if (rows[idx]?.method === 'deposit') {
        rows[idx].amount = '';
        rows[idx].currency = defaultCurrency;
      }
      return { ...prev, chargePayments: rows };
    });
  }

  function addPaymentRow() {
    setForm((prev) => {
      const rows = Array.isArray(prev.chargePayments) ? [...prev.chargePayments] : [];
      rows.push({ method: 'cash', amount: '', currency: defaultCurrency, note: '' });
      return { ...prev, chargePayments: rows };
    });
  }

  function removePaymentRow(idx) {
    setForm((prev) => {
      const rows = Array.isArray(prev.chargePayments) ? [...prev.chargePayments] : [];
      rows.splice(idx, 1);
      return {
        ...prev,
        chargePayments: rows.length ? rows : [{ method: 'cash', amount: '', currency: defaultCurrency, note: '' }],
      };
    });
  }

  function setDepositOutcome(outcome) {
    setForm((prev) => {
      const next = { ...prev, depositOutcome: outcome };

      if (outcome === 'full_release') {
        // Full release: no deduction and no remaining hold.
        next.depositOutcomeReason = '';
        next.depositKeptAmount = '';
        next.depositOnHoldAmount = '';
        next.depositToReturnCurrency = normCurrency(prev.depositCurrencyCollected, defaultCurrency);
        next.depositToReturnAmount = prev.depositAmount || '';
        return next;
      }

      if (outcome === 'partial_release') {
        // Partial release: user records the amount taken; refund is computed below.
        next.depositOutcomeReason = next.depositOutcomeReason || '';
        next.depositOnHoldAmount = '';
        next.depositToReturnCurrency = normCurrency(prev.depositCurrencyCollected, defaultCurrency);
        return next;
      }

      if (outcome === 'on_hold') {
        // On hold: no refund recorded now; deduction and remaining hold are separate.
        next.depositToReturnAmount = '';
        next.depositToReturnCurrency = normCurrency(prev.depositCurrencyCollected, defaultCurrency);
        next.depositReleaseMethod = '';
        next.depositOutcomeReason = next.depositOutcomeReason || '';
        return next;
      }

      return next;
    });
  }

  // -----------------------------
  // Auto: deposit allocation.
  // - If partial_release: returned = received - taken (same currency)
  // - If on_hold: held = received - taken (same currency)
  // - If deposit received via card: default release method to card_refund
  // -----------------------------
  useEffect(() => {
    const depCur = normCurrency(form.depositCurrencyCollected, defaultCurrency);
    const depAmt = safeNum(form.depositAmount);
    const kept = Math.max(0, safeNum(form.depositKeptAmount));

    if (form.depositOutcome === 'partial_release') {
      const computedReturn = Math.max(0, depAmt - kept);
      const shouldAutoMethod = form.depositReceivedMethod === 'card' && !String(form.depositReleaseMethod || '').trim();

      setForm((prev) => ({
        ...prev,
        depositToReturnCurrency: depCur,
        depositToReturnAmount: depAmt ? String(computedReturn.toFixed(2)) : prev.depositToReturnAmount,
        depositOnHoldAmount: '',
        ...(shouldAutoMethod ? { depositReleaseMethod: 'card_refund' } : {}),
      }));
    }

    if (form.depositOutcome === 'full_release') {
      // keep it consistent even if user edits deposit amount later
      if (String(form.depositToReturnCurrency || '') !== depCur || String(form.depositToReturnAmount || '') !== String(form.depositAmount || '')) {
        setForm((prev) => ({
          ...prev,
          depositToReturnCurrency: depCur,
          depositToReturnAmount: prev.depositAmount || '',
          depositKeptAmount: '',
          depositOnHoldAmount: '',
        }));
      }
    }

    if (form.depositOutcome === 'on_hold') {
      const computedHold = Math.max(0, depAmt - kept);
      setForm((prev) => ({
        ...prev,
        depositToReturnCurrency: depCur,
        depositToReturnAmount: '',
        depositOnHoldAmount: depAmt ? String(computedHold.toFixed(2)) : '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.depositOutcome,
    form.depositAmount,
    form.depositCurrencyCollected,
    form.depositKeptAmount,
    form.depositReceivedMethod,
    form.depositReleaseMethod,
    defaultCurrency,
  ]);

  // -----------------------------
  // Prefill from existing handover
  // -----------------------------
  useEffect(() => {
    if (!existingHandover || initializedFromExisting) return;

    let parsed = {};
    try {
      if (existingHandover.equipmentNotes) parsed = JSON.parse(existingHandover.equipmentNotes) || {};
    } catch (e) {
      console.warn('Failed to parse equipmentNotes from existing dropoff handover', e);
    }

    setForm((prev) => {
      const merged = { ...prev, ...parsed };

      merged.charges = { ...prev.charges, ...(parsed.charges || {}) };
      for (const k of ['delivery', 'extrasDamage', 'carWash', 'fuel', 'bodyDamage', 'fines']) {
        merged.charges[k] = { ...(prev.charges?.[k] || {}), ...((parsed.charges && parsed.charges[k]) || {}) };
      }
      merged.charges.extraDays = { ...(prev.charges?.extraDays || {}), ...((parsed.charges && parsed.charges.extraDays) || {}) };

      if (!Array.isArray(merged.chargePayments) || merged.chargePayments.length === 0) {
        merged.chargePayments = [{ method: 'cash', amount: '', currency: defaultCurrency, note: '' }];
      }

      merged.extrasReturnedCarSeat =
        typeof merged.extrasReturnedCarSeat === 'boolean' ? merged.extrasReturnedCarSeat : prev.extrasReturnedCarSeat;
      merged.extrasReturnedBooster =
        typeof merged.extrasReturnedBooster === 'boolean' ? merged.extrasReturnedBooster : prev.extrasReturnedBooster;
      merged.extrasReturnedSnowChains =
        typeof merged.extrasReturnedSnowChains === 'boolean'
          ? merged.extrasReturnedSnowChains
          : prev.extrasReturnedSnowChains;

      return merged;
    });

    setSubmitted(true);
    setInitializedFromExisting(true);
  }, [existingHandover, initializedFromExisting, defaultCurrency]);

  // Ensure default currencies when location changes
  useEffect(() => {
    const def = location === 'AL' ? 'EUR' : 'USD';

    setForm((prev) => {
      const charges = { ...(prev.charges || {}) };

      function ensureChargeCur(k) {
        const obj = { ...(charges[k] || {}) };
        if (!String(obj.currency || '').trim()) obj.currency = def;
        charges[k] = obj;
      }

      ensureChargeCur('delivery');
      ensureChargeCur('extrasDamage');
      ensureChargeCur('carWash');
      ensureChargeCur('fuel');
      ensureChargeCur('bodyDamage');
      ensureChargeCur('fines');

      charges.extraDays = { ...(charges.extraDays || {}) };
      if (!String(charges.extraDays.currency || '').trim()) charges.extraDays.currency = def;

      const payments = Array.isArray(prev.chargePayments)
        ? prev.chargePayments.map((p) => {
            const cur = String(p?.currency || '').trim() ? p.currency : def;
            return { ...p, currency: cur };
          })
        : [{ method: 'cash', amount: '', currency: def, note: '' }];

      return {
        ...prev,
        depositCurrencyCollected: prev.depositCurrencyCollected || def,
        depositToReturnCurrency: prev.depositToReturnCurrency || def,
        charges,
        chargePayments: payments,
      };
    });
  }, [location]);

  // Load S3 URLs for existing photos
  useEffect(() => {
    if (!existingHandover) {
      setExistingCarPhotoUrls([]);
      setExistingDamagePhotoUrls([]);
      return;
    }

    const carKeys = Array.isArray(existingHandover.carPhotoKeys) ? existingHandover.carPhotoKeys : [];
    const damageKeys = Array.isArray(existingHandover.damagePhotoKeys) ? existingHandover.damagePhotoKeys : [];

    async function load() {
      const loadUrls = async (keys) => {
        const results = await Promise.all(
          keys.map(async (key) => {
            try {
              const { url } = await getUrl({ key });
              return { key, url: url.toString() };
            } catch (err) {
              console.warn('Failed to get URL for key', key, err);
              return null;
            }
          })
        );
        return results.filter(Boolean);
      };

      const [carUrls, damageUrls] = await Promise.all([loadUrls(carKeys), loadUrls(damageKeys)]);
      setExistingCarPhotoUrls(carUrls);
      setExistingDamagePhotoUrls(damageUrls);
    }

    load();
  }, [existingHandover]);

  // Prefill from reservation + pickup handover
  useEffect(() => {
    if (initializedFromReservation || initializedFromExisting) return;

const dropoffIso = reservation?.dropoffAt || reservation?.end || null;
const { date: dropoffDate, time: dropoffTime } = splitCompanyDateTime(dropoffIso, companyTz);

    let firstName = '';
    let lastName = '';
    if (reservation?.customerName) {
      const parts = reservation.customerName.trim().split(' ');
      firstName = parts[0] || '';
      lastName = parts.slice(1).join(' ');
    }

    let pickupFuel = pickupFuelLevel || '';
    if (!pickupFuel && pickupHandover?.fuelLevelPercent != null) {
      const p = pickupHandover.fuelLevelPercent;
      if (p <= 25) pickupFuel = '1/4';
      else if (p <= 50) pickupFuel = '1/2';
      else if (p <= 75) pickupFuel = '3/4';
      else pickupFuel = 'full';
    }

    let pickupParsed = {};
    let lesseeEmail = '';
    let lesseePhone = '';
    let idNumber = '';
    let drivingLicenseNumber = '';
    let drivingLicenseCountry = '';

    if (pickupHandover) {
      try {
        if (pickupHandover.equipmentNotes) pickupParsed = JSON.parse(pickupHandover.equipmentNotes) || {};
      } catch (e) {
        console.warn('Failed to parse pickup equipmentNotes', e);
      }

      lesseeEmail = pickupHandover.customerEmail || pickupParsed.lesseeEmail || '';
      lesseePhone = pickupHandover.customerPhone || pickupParsed.lesseePhone || '';
      idNumber = pickupParsed.idNumber || '';
      drivingLicenseNumber = pickupHandover.licenseNumber || pickupParsed.drivingLicenseNumber || '';
      drivingLicenseCountry = pickupParsed.drivingLicenseCountry || '';
    }

    const depositAmount = reservation?.depositAmount != null ? String(reservation.depositAmount) : '';
    const depositCurrencyCollected = reservation?.depositCurrency || '';

    setForm((prev) => ({
      ...prev,
      lesseeFirstName: firstName || prev.lesseeFirstName,
      lesseeLastName: lastName || prev.lesseeLastName,
      lesseeEmail: lesseeEmail || prev.lesseeEmail,
      lesseePhone: lesseePhone || prev.lesseePhone,
      idNumber: idNumber || prev.idNumber,
      drivingLicenseNumber: drivingLicenseNumber || prev.drivingLicenseNumber,
      drivingLicenseCountry: drivingLicenseCountry || prev.drivingLicenseCountry,

      dropoffDate: dropoffDate || prev.dropoffDate,
      dropoffTime: dropoffTime || prev.dropoffTime,
      dropoffLocation:
        reservation?.dropoffLocation ||
        prev.dropoffLocation ||
        (isAlbania ? 'Tirana International Airport (TIA)' : 'Tbilisi Airport'),

      fuelDeliveredAmount: pickupFuel || prev.fuelDeliveredAmount || '',

      depositAmount: depositAmount || prev.depositAmount,
      depositCurrencyCollected: depositCurrencyCollected || prev.depositCurrencyCollected,
    }));

    setFuelDeliveredLocked(!!pickupFuel);
    setInitializedFromReservation(true);
 }, [
   reservation,
   pickupFuelLevel,
   pickupHandover,
   initializedFromReservation,
   initializedFromExisting,
   isAlbania,
   companyTz,
 ]);
  // -----------------------------
  // Totals (SIMPLIFIED)
  // - Charges totals by currency
  // - Settled totals by currency = outside payments + deposit taken (if any)
  // - Remaining by currency = charges - settled
  // -----------------------------
  const chargeItems = useMemo(() => {
    const c = form.charges || {};
    return [
      { key: 'delivery', amount: c.delivery?.amount, currency: c.delivery?.currency },
      { key: 'extrasDamage', amount: c.extrasDamage?.amount, currency: c.extrasDamage?.currency },
      { key: 'carWash', amount: c.carWash?.amount, currency: c.carWash?.currency },
      { key: 'fuel', amount: c.fuel?.amount, currency: c.fuel?.currency },
      { key: 'bodyDamage', amount: c.bodyDamage?.amount, currency: c.bodyDamage?.currency },
      { key: 'fines', amount: c.fines?.amount, currency: c.fines?.currency },
      { key: 'extraDays', amount: c.extraDays?.amount, currency: c.extraDays?.currency },
    ];
  }, [form.charges]);

  const totalsChargesByCurrency = useMemo(() => sumByCurrency(chargeItems), [chargeItems]);

  const paymentItemsOutsideDeposit = useMemo(() => {
    const rows = Array.isArray(form.chargePayments) ? form.chargePayments : [];
    return rows
      .map((r) => ({
        method: r.method || 'cash',
        amount: r.amount,
        currency: r.currency || defaultCurrency,
      }))
      .filter((x) => x.method !== 'deposit')
      .filter((x) => String(x.amount || '').trim() !== '');
  }, [form.chargePayments, defaultCurrency]);

  const totalsPaidByCurrency = useMemo(() => sumByCurrency(paymentItemsOutsideDeposit), [paymentItemsOutsideDeposit]);

  const depositDeductionByCurrency = useMemo(() => {
    const depCur = normCurrency(form.depositCurrencyCollected, defaultCurrency);
    const depAmt = safeNum(form.depositAmount);
    const hasTakenAmount = String(form.depositKeptAmount || '').trim() !== '';

    // Explicit amount taken from deposit, independent of release/hold outcome.
    if ((form.depositOutcome === 'partial_release' || form.depositOutcome === 'on_hold') && hasTakenAmount) {
      const kept = Math.max(0, safeNum(form.depositKeptAmount));
      if (!kept) return {};
      return { [depCur]: kept };
    }

    if (!form.depositOutcome || form.depositOutcome === 'full_release' || form.depositOutcome === 'on_hold') return {};

    // Fallback for older partial-release records: infer from received - returned.
    const retCur = normCurrency(form.depositToReturnCurrency, depCur);
    const retAmt = safeNum(form.depositToReturnAmount);

    if (!depAmt) return {};
    if (retAmt && depCur !== retCur) return {};

    const ded = Math.max(0, depAmt - (retAmt || 0));
    if (!ded) return {};
    return { [depCur]: ded };
  }, [
    form.depositOutcome,
    form.depositKeptAmount,
    form.depositCurrencyCollected,
    form.depositAmount,
    form.depositToReturnCurrency,
    form.depositToReturnAmount,
    defaultCurrency,
  ]);

  const depositHoldByCurrency = useMemo(() => {
    if (form.depositOutcome !== 'on_hold') return {};
    const depCur = normCurrency(form.depositCurrencyCollected, defaultCurrency);
    const depAmt = safeNum(form.depositAmount);
    const taken = Math.max(0, safeNum(form.depositKeptAmount));
    const explicitHold = String(form.depositOnHoldAmount || '').trim();
    const hold = explicitHold !== '' ? safeNum(form.depositOnHoldAmount) : Math.max(0, depAmt - taken);
    if (!hold) return {};
    return { [depCur]: hold };
  }, [
    form.depositOutcome,
    form.depositCurrencyCollected,
    form.depositAmount,
    form.depositKeptAmount,
    form.depositOnHoldAmount,
    defaultCurrency,
  ]);

  const totalsSettledByCurrency = useMemo(() => {
    const out = { ...(totalsPaidByCurrency || {}) };
    for (const [cur, v] of Object.entries(depositDeductionByCurrency || {})) {
      out[cur] = (out[cur] || 0) + safeNum(v);
    }
    return out;
  }, [totalsPaidByCurrency, depositDeductionByCurrency]);

  const remainingByCurrency = useMemo(() => {
    const out = {};
    const all = new Set([...Object.keys(totalsChargesByCurrency || {}), ...Object.keys(totalsSettledByCurrency || {})]);
    for (const cur of all) out[cur] = safeNum(totalsChargesByCurrency?.[cur]) - safeNum(totalsSettledByCurrency?.[cur]);
    return out;
  }, [totalsChargesByCurrency, totalsSettledByCurrency]);

  const chargesPairs = dictToSortedPairs(totalsChargesByCurrency);
  const settledPairs = dictToSortedPairs(totalsSettledByCurrency);
  const remainingPairs = dictToSortedPairs(remainingByCurrency);
  const depositHoldPairs = dictToSortedPairs(depositHoldByCurrency);
  const depositCurrencyLabel = normCurrency(form.depositCurrencyCollected, defaultCurrency);

  // -----------------------------
  // Submit
  // -----------------------------
  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;

    if (readOnly) {
      showAlert('error', 'Handover locked', 'This return inspection is read-only and cannot be edited.');
      return;
    }

    if (!form.dropoffDate || !form.dropoffTime) {
      showAlert('error', 'Missing return time', 'Return date and time are required.');
      return;
    }

    if (!reservation?.CompanyId) {
      showAlert('error', 'Missing agency', 'Reservation is missing CompanyId – cannot submit handover.');
      return;
    }

    if (!form.lesseeSignatureDataUrl && !form.customerRefusedToSign) {
      showAlert('error', 'Signature required', 'Lessee return signature is required unless the customer refused to sign.');
      return;
    }

    if (pendingPhotoUploads > 0) {
      showAlert(
        'error',
        'Uploads still running',
        `Wait for ${pendingPhotoUploads} photo upload${pendingPhotoUploads === 1 ? '' : 's'} to finish before saving.`
      );
      return;
    }

    if (failedPhotoUploads > 0) {
      showAlert(
        'error',
        'Photo uploads failed',
        `Remove or re-upload the ${failedPhotoUploads} failed photo${failedPhotoUploads === 1 ? '' : 's'} before saving.`
      );
      return;
    }

    try {
      setSubmitting(true);
      setSubmitStage(null);
      setSubmitPct(0);
      setSubmitDetail('');

      setStage('prep', 5, 'Preparing return inspection…');

      const carPhotoKeys = [
        ...uploadedItemsToKeys(washCarUploads),
        ...uploadedItemsToKeys(fuelOdoUploads),
      ];
      const damagePhotoKeys = [
        ...uploadedItemsToKeys(extrasDamageUploads),
        ...uploadedItemsToKeys(bodyDamageUploads),
      ];

      setStage('upload_sig', 45, 'Uploading signature…');
      let signatureKey = null;
      if (form.lesseeSignatureDataUrl && !form.customerRefusedToSign) {
        const sigFile = dataUrlToFile(form.lesseeSignatureDataUrl, `lessee_signature_dropoff_${reservationId}.png`);
        if (sigFile) {
          const sigKeys = await uploadFiles('handover/dropoff/signature', reservationId, [sigFile]);
          signatureKey = sigKeys[0] || null;
        }
      }

      setStage('save', 78, 'Saving handover record…');

      const car = (cars || []).find((c) => c.id === reservation?.carId);
      const carName = car?.name || null;
      const carPlate = car?.plate || null;

      const totalsSnapshot = {
        chargesByCurrency: totalsChargesByCurrency,
        settledByCurrency: totalsSettledByCurrency,
        remainingByCurrency,
        impliedDepositDeductionByCurrency: depositDeductionByCurrency,
        depositHoldByCurrency,
        computedAt: new Date().toISOString(),
      };

      const input = {
        CompanyId: reservation.CompanyId,
        reservationId,
        type: 'DROPOFF',
        filledBySub: claims?.sub || null,
        filledByEmail: claims?.email || null,
        customerName: `${form.lesseeFirstName} ${form.lesseeLastName}`.trim().replace(/\s+/g, ' ') || null,
        customerEmail: form.lesseeEmail || null,
        customerPhone: form.lesseePhone || null,
        licenseNumber: form.drivingLicenseNumber || form.idNumber || null,
        carId: reservation?.carId || null,
        carName,
        carPlate,
        odometerKm: null,
        fuelLevelPercent: null,
        exteriorCondition: null,
        interiorCondition: null,
        damageNotes: form.damageDescription || null,
        equipmentNotes: JSON.stringify({ ...form, totals: totalsSnapshot }),
        carPhotoKeys,
        damagePhotoKeys,
        signatureKey,
        ipAddress: null,
        signedAt: new Date().toISOString(),
      };

      const result = await client.graphql({
        query: SubmitHandoverForm,
        variables: { input },
        authMode: 'userPool',
      });

      const ok = result?.data?.submitHandoverForm;
      if (!ok) throw new Error('Mutation returned false');

      setStage('update_res', 92, 'Updating reservation checklist…');
      if (reservation?.id) {
        try {
          const updateInput = {
            id: reservation.id,
            dropoffChecklistStatus: 'COMPLETED',
            ...(reservation._version != null ? { _version: reservation._version } : {}),
          };
          await client.graphql({
            query: UpdateReservationChecklist,
            variables: { input: updateInput },
            authMode: 'userPool',
          });
        } catch (e2) {
          console.warn('Failed to update reservation dropoffChecklistStatus', e2);
        }
      }

      setStage('done', 100, 'Done');
      setSubmitted(true);

      showAlert('success', 'Return inspection saved', 'Saved successfully. Email will be sent shortly (if configured).');
    } catch (err) {
      console.error('Dropoff handover submit failed', err);
      showAlert('error', 'Save failed', 'Failed to submit return inspection. Check console for details.');
    } finally {
      setSubmitting(false);
    }
  }

  // Read-only image grid
  const renderImageGrid = (items, emptyText) => {
    if (!items || items.length === 0) {
      return <div style={{ fontSize: 12, color: COLORS.gray[500], marginTop: 4 }}>{emptyText}</div>;
    }
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
        {items.map((p) => (
          <a
            key={p.key}
            href={p.url}
            target="_blank"
            rel="noreferrer"
            style={{ display: 'inline-block', borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLORS.gray[200]}` }}
          >
            <img src={p.url} alt="" style={{ width: 96, height: 72, objectFit: 'cover', display: 'block' }} />
          </a>
        ))}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 20, width: '100%', maxWidth: '100%' }}>
      <fieldset disabled={readOnly || submitting || submitted} style={readOnly ? { opacity: 0.85 } : undefined}>
        {/* Basic info */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Customer & return details</h3>

          <div style={twoColResponsive}>
            <label>
              <span style={labelStyle}>Lessee first name</span>
              <input style={inputStyle} value={form.lesseeFirstName} onChange={(e) => updateField('lesseeFirstName', e.target.value)} />
            </label>
            <label>
              <span style={labelStyle}>Lessee last name</span>
              <input style={inputStyle} value={form.lesseeLastName} onChange={(e) => updateField('lesseeLastName', e.target.value)} />
            </label>
          </div>

          <div style={{ ...twoColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Lessee email</span>
              <input type="email" style={inputStyle} value={form.lesseeEmail} onChange={(e) => updateField('lesseeEmail', e.target.value)} />
            </label>
            <label>
              <span style={labelStyle}>Mobile number</span>
              <input type="tel" style={inputStyle} value={form.lesseePhone} onChange={(e) => updateField('lesseePhone', e.target.value)} />
            </label>
          </div>

          <div style={{ ...twoColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>ID / passport number</span>
              <input style={inputStyle} value={form.idNumber} onChange={(e) => updateField('idNumber', e.target.value)} />
            </label>
            <label>
              <span style={labelStyle}>Driving licence number</span>
              <input style={inputStyle} value={form.drivingLicenseNumber} onChange={(e) => updateField('drivingLicenseNumber', e.target.value)} />
            </label>
          </div>

          <div style={{ ...twoColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Driving licence issued in</span>
              <input style={inputStyle} value={form.drivingLicenseCountry} onChange={(e) => updateField('drivingLicenseCountry', e.target.value)} />
            </label>
            <div />
          </div>

          <div style={{ ...twoColResponsive, marginTop: 10 }}>
            <div>
              <span style={labelStyle}>Drop-off date & time</span>
              <div style={twoColResponsive}>
                <input type="date" style={inputStyle} value={form.dropoffDate} onChange={(e) => updateField('dropoffDate', e.target.value)} />
                <input type="time" style={inputStyle} value={form.dropoffTime} onChange={(e) => updateField('dropoffTime', e.target.value)} />
              </div>
            </div>
            <label>
              <span style={labelStyle}>Drop-off location</span>
              <input style={inputStyle} value={form.dropoffLocation} onChange={(e) => updateField('dropoffLocation', e.target.value)} />
            </label>
          </div>

          <div style={{ marginTop: 10 }}>
            <span style={labelStyle}>Change of drop-off location</span>
            <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="dropoffLocationChanged" checked={form.dropoffLocationChanged === 'yes'} onChange={() => updateField('dropoffLocationChanged', 'yes')} />
                Yes
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="dropoffLocationChanged" checked={form.dropoffLocationChanged === 'no'} onChange={() => updateField('dropoffLocationChanged', 'no')} />
                No
              </label>
            </div>
          </div>

          {form.dropoffLocationChanged === 'yes' && (
            <div style={{ ...threeColResponsive, marginTop: 8 }}>
              <label style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
                <span style={labelStyle}>New drop-off location</span>
                <input style={inputStyle} value={form.newDropoffLocation} onChange={(e) => updateField('newDropoffLocation', e.target.value)} />
              </label>

              <label>
                <span style={labelStyle}>Delivery charge amount</span>
                <input type="number" step="0.01" style={inputStyle} value={form.charges?.delivery?.amount || ''} onChange={(e) => updateCharge('delivery', { amount: e.target.value })} />
              </label>

              <label>
                <span style={labelStyle}>Currency</span>
                <select style={inputStyle} value={form.charges?.delivery?.currency || defaultCurrency} onChange={(e) => updateCharge('delivery', { currency: e.target.value })}>
                  {CURRENCY_OPTIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </section>

        {/* Extras */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Extras return check</h3>

          <div style={{ fontSize: 12, color: COLORS.gray[500], marginBottom: 6 }}>
            Untick items that were <b>not</b> returned by the customer.
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 13 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={form.extrasReturnedCarSeat} onChange={(e) => updateField('extrasReturnedCarSeat', e.target.checked)} />
              Child seat
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={form.extrasReturnedBooster} onChange={(e) => updateField('extrasReturnedBooster', e.target.checked)} />
              Booster
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={form.extrasReturnedSnowChains} onChange={(e) => updateField('extrasReturnedSnowChains', e.target.checked)} />
              Snow chains
            </label>
          </div>

          <div style={{ marginTop: 10 }}>
            <span style={labelStyle}>Damage in returned extras</span>
            <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="extrasDamage" checked={form.extrasDamage === 'yes'} onChange={() => updateField('extrasDamage', 'yes')} />
                Yes
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="extrasDamage" checked={form.extrasDamage === 'no'} onChange={() => updateField('extrasDamage', 'no')} />
                No
              </label>
            </div>
          </div>

          {readOnly ? (
            <div style={{ marginTop: 8 }}>
              <span style={labelStyle}>Extras damage photos</span>
              {renderImageGrid(existingDamagePhotoUrls, 'No extras damage photos uploaded for this handover.')}
            </div>
          ) : (
            <FileUploadWithPreview
              label="Extras damage photos"
              uploads={extrasDamageUploads}
              onUploadsChange={setExtrasDamageUploads}
              uploadPrefix="handover/dropoff/damage"
              reservationId={reservationId}
              disabled={submitted}
              preserveUploadedOnUnmount={submitted}
            />
          )}

          <div style={{ ...threeColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Extras damage charge amount</span>
              <input type="number" step="0.01" style={inputStyle} value={form.charges?.extrasDamage?.amount || ''} onChange={(e) => updateCharge('extrasDamage', { amount: e.target.value })} />
            </label>
            <label>
              <span style={labelStyle}>Currency</span>
              <select style={inputStyle} value={form.charges?.extrasDamage?.currency || defaultCurrency} onChange={(e) => updateCharge('extrasDamage', { currency: e.target.value })}>
                {CURRENCY_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <div />
          </div>
        </section>

        {/* Car wash */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Car wash condition</h3>

          <div>
            <span style={labelStyle}>Is the car returned washed?</span>
            <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="carWashed" checked={form.carWashed === 'yes'} onChange={() => updateField('carWashed', 'yes')} />
                Yes
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="carWashed" checked={form.carWashed === 'no'} onChange={() => updateField('carWashed', 'no')} />
                No
              </label>
            </div>
          </div>

          {readOnly ? (
            <div style={{ marginTop: 8 }}>
              <span style={labelStyle}>Car wash / condition photos</span>
              {renderImageGrid(existingCarPhotoUrls, 'No car wash / condition photos uploaded for this handover.')}
            </div>
          ) : (
            <FileUploadWithPreview
              label={form.carWashed === 'yes' ? 'Washed car photos' : 'Unwashed / dirty car photos'}
              uploads={washCarUploads}
              onUploadsChange={setWashCarUploads}
              uploadPrefix="handover/dropoff/car"
              reservationId={reservationId}
              disabled={submitted}
              preserveUploadedOnUnmount={submitted}
            />
          )}

          <div style={{ ...threeColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Car wash amount</span>
              <input type="number" step="0.01" style={inputStyle} value={form.charges?.carWash?.amount || ''} onChange={(e) => updateCharge('carWash', { amount: e.target.value })} />
            </label>
            <label>
              <span style={labelStyle}>Currency</span>
              <select style={inputStyle} value={form.charges?.carWash?.currency || defaultCurrency} onChange={(e) => updateCharge('carWash', { currency: e.target.value })}>
                {CURRENCY_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <div />
          </div>
        </section>

        {/* Fuel */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Fuel condition</h3>

          <div>
            <span style={labelStyle}>Fuel status</span>
            <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="fuelCondition" checked={form.fuelCondition === 'like_for_like'} onChange={() => updateField('fuelCondition', 'like_for_like')} />
                Like for like
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="fuelCondition" checked={form.fuelCondition === 'discrepancy'} onChange={() => updateField('fuelCondition', 'discrepancy')} />
                Discrepancy
              </label>
            </div>
          </div>

          <div style={{ ...twoColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Fuel at delivery (from pickup form)</span>
              <select
                style={{ ...inputStyle, background: COLORS.gray[100], cursor: fuelDeliveredLocked ? 'not-allowed' : 'default' }}
                value={form.fuelDeliveredAmount}
                onChange={(e) => updateField('fuelDeliveredAmount', e.target.value)}
                disabled={fuelDeliveredLocked}
              >
                <option value="">Select…</option>
                <option value="1/4">¼ tank</option>
                <option value="1/2">½ tank</option>
                <option value="3/4">¾ tank</option>
                <option value="full">Full tank</option>
              </select>
              {fuelDeliveredLocked && <div style={{ fontSize: 11, color: COLORS.gray[500], marginTop: 2 }}>Value locked from pick-up handover.</div>}
            </label>

            <label>
              <span style={labelStyle}>Fuel at return</span>
              <select style={inputStyle} value={form.fuelReturnedAmount} onChange={(e) => updateField('fuelReturnedAmount', e.target.value)}>
                <option value="">Select…</option>
                <option value="1/4">¼ tank</option>
                <option value="1/2">½ tank</option>
                <option value="3/4">¾ tank</option>
                <option value="full">Full tank</option>
              </select>
            </label>
          </div>

          {readOnly ? (
            <div style={{ marginTop: 8 }}>
              <span style={labelStyle}>Odometer / fuel photos</span>
              {renderImageGrid(existingCarPhotoUrls, 'No odometer / fuel photos uploaded for this handover.')}
            </div>
          ) : (
            <FileUploadWithPreview
              label="Odometer / fuel photos"
              uploads={fuelOdoUploads}
              onUploadsChange={setFuelOdoUploads}
              uploadPrefix="handover/dropoff/car"
              reservationId={reservationId}
              disabled={submitted}
              preserveUploadedOnUnmount={submitted}
            />
          )}

          <div style={{ ...threeColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Fuel charge amount</span>
              <input type="number" step="0.01" style={inputStyle} value={form.charges?.fuel?.amount || ''} onChange={(e) => updateCharge('fuel', { amount: e.target.value })} />
            </label>
            <label>
              <span style={labelStyle}>Currency</span>
              <select style={inputStyle} value={form.charges?.fuel?.currency || defaultCurrency} onChange={(e) => updateCharge('fuel', { currency: e.target.value })}>
                {CURRENCY_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <div />
          </div>
        </section>

        {/* Exterior body */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Exterior body condition</h3>

          <div>
            <span style={labelStyle}>Returned in same body condition?</span>
            <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="bodySameCondition" checked={form.bodySameCondition === 'yes'} onChange={() => updateField('bodySameCondition', 'yes')} />
                Yes
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="bodySameCondition" checked={form.bodySameCondition === 'no'} onChange={() => updateField('bodySameCondition', 'no')} />
                No
              </label>
            </div>
          </div>

          {readOnly ? (
            <div style={{ marginTop: 8 }}>
              <span style={labelStyle}>Vehicle damage photos</span>
              {renderImageGrid(existingDamagePhotoUrls, 'No vehicle damage photos uploaded for this handover.')}
            </div>
          ) : (
            <FileUploadWithPreview
              label="Vehicle damage photos"
              uploads={bodyDamageUploads}
              onUploadsChange={setBodyDamageUploads}
              uploadPrefix="handover/dropoff/damage"
              reservationId={reservationId}
              disabled={submitted}
              preserveUploadedOnUnmount={submitted}
            />
          )}

          <label style={{ marginTop: 10, display: 'block' }}>
            <span style={labelStyle}>Damage description</span>
            <textarea style={textareaStyle} value={form.damageDescription} onChange={(e) => updateField('damageDescription', e.target.value)} />
          </label>

          <div style={{ ...threeColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Damage charge amount</span>
              <input type="number" step="0.01" style={inputStyle} value={form.charges?.bodyDamage?.amount || ''} onChange={(e) => updateCharge('bodyDamage', { amount: e.target.value })} />
            </label>
            <label>
              <span style={labelStyle}>Currency</span>
              <select style={inputStyle} value={form.charges?.bodyDamage?.currency || defaultCurrency} onChange={(e) => updateCharge('bodyDamage', { currency: e.target.value })}>
                {CURRENCY_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <div />
          </div>
        </section>

        {/* FINES & EXTRA DAYS (OWN SECTION) */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Fines & extra days</h3>
          <div style={{ fontSize: 12, color: COLORS.gray[500] }}>
            Enter these in the <b>currency actually collected</b>. No conversion.
          </div>

          <div style={{ ...threeColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Fines amount</span>
              <input type="number" step="0.01" style={inputStyle} value={form.charges?.fines?.amount || ''} onChange={(e) => updateCharge('fines', { amount: e.target.value })} />
            </label>
            <label>
              <span style={labelStyle}>Currency</span>
              <select style={inputStyle} value={form.charges?.fines?.currency || defaultCurrency} onChange={(e) => updateCharge('fines', { currency: e.target.value })}>
                {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <div />
          </div>

          <div style={{ ...threeColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Extra days (optional)</span>
              <input type="number" style={inputStyle} value={form.charges?.extraDays?.days || ''} onChange={(e) => updateChargeExtraDays({ days: e.target.value })} />
            </label>
            <label>
              <span style={labelStyle}>Extra days charge amount</span>
              <input type="number" step="0.01" style={inputStyle} value={form.charges?.extraDays?.amount || ''} onChange={(e) => updateChargeExtraDays({ amount: e.target.value })} />
            </label>
            <label>
              <span style={labelStyle}>Currency</span>
              <select style={inputStyle} value={form.charges?.extraDays?.currency || defaultCurrency} onChange={(e) => updateChargeExtraDays({ currency: e.target.value })}>
                {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
        </section>

        {/* Settlement (SIMPLIFIED TOTALS + PARTIAL DEPOSIT FLOW) */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Payments & deposit</h3>

          <div style={{ fontSize: 12, color: COLORS.gray[500] }}>
            Simple view: <b>Charges</b> vs <b>Settled</b> vs <b>Remaining</b>, per currency.
          </div>

          <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
            <div style={{ border: `1px solid ${COLORS.gray[200]}`, borderRadius: 12, padding: 12, background: COLORS.gray[50] }}>
              <div style={{ fontSize: 12, color: COLORS.gray[500], fontWeight: 800, marginBottom: 6 }}>Charges (grand total)</div>
              {chargesPairs.length === 0 ? (
                <div style={{ fontSize: 12, color: COLORS.gray[500] }}>No charges entered.</div>
              ) : (
                chargesPairs.map(([cur, val]) => (
                  <div key={cur} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0' }}>
                    <span>{cur}</span>
                    <span style={{ fontWeight: 900 }}>{fmt(val)}</span>
                  </div>
                ))
              )}
            </div>

            <div style={{ border: `1px solid ${COLORS.gray[200]}`, borderRadius: 12, padding: 12, background: '#fff' }}>
              <div style={{ fontSize: 12, color: COLORS.gray[500], fontWeight: 800, marginBottom: 6 }}>Settled (outside payments + deposit taken)</div>
              {settledPairs.length === 0 ? (
                <div style={{ fontSize: 12, color: COLORS.gray[500] }}>Nothing settled yet.</div>
              ) : (
                settledPairs.map(([cur, val]) => (
                  <div key={cur} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0' }}>
                    <span>{cur}</span>
                    <span style={{ fontWeight: 900 }}>{fmt(val)}</span>
                  </div>
                ))
              )}
            </div>

            <div style={{ border: `1px solid ${COLORS.gray[200]}`, borderRadius: 12, padding: 12, background: '#fff' }}>
              <div style={{ fontSize: 12, color: COLORS.gray[500], fontWeight: 800, marginBottom: 6 }}>Remaining</div>
              {remainingPairs.length === 0 ? (
                <div style={{ fontSize: 12, color: COLORS.gray[500] }}>No comparison yet.</div>
              ) : (
                remainingPairs.map(([cur, val]) => (
                  <div key={cur} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0' }}>
                    <span>{cur}</span>
                    <span style={{ fontWeight: 900, color: safeNum(val) > 0 ? COLORS.danger : COLORS.success }}>{fmt(val)}</span>
                  </div>
                ))
              )}
            </div>

            {depositHoldPairs.length > 0 && (
              <div style={{ border: `1px solid ${COLORS.warningLight}`, borderRadius: 12, padding: 12, background: COLORS.warningLight }}>
                <div style={{ fontSize: 12, color: '#92400e', fontWeight: 800, marginBottom: 6 }}>Deposit still on hold</div>
                {depositHoldPairs.map(([cur, val]) => (
                  <div key={cur} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', color: '#78350f' }}>
                    <span>{cur}</span>
                    <span style={{ fontWeight: 900 }}>{fmt(val)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <hr style={{ border: 0, borderTop: `1px solid ${COLORS.gray[200]}`, margin: '14px 0' }} />

          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 900, color: COLORS.gray[900] }}>Deposit received (pickup)</h4>
          <div style={{ ...threeColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Amount</span>
              <input type="number" step="0.01" style={inputStyle} value={form.depositAmount} onChange={(e) => updateField('depositAmount', e.target.value)} />
            </label>

            <label>
              <span style={labelStyle}>Currency</span>
              <select style={inputStyle} value={form.depositCurrencyCollected} onChange={(e) => updateField('depositCurrencyCollected', e.target.value)}>
                {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>

            <label>
              <span style={labelStyle}>Received via</span>
              <select style={inputStyle} value={form.depositReceivedMethod} onChange={(e) => updateField('depositReceivedMethod', e.target.value)}>
                <option value="">Select…</option>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="bank">Bank transfer</option>
                <option value="other">Other</option>
              </select>
            </label>
          </div>

          <hr style={{ border: 0, borderTop: `1px solid ${COLORS.gray[200]}`, margin: '14px 0' }} />

          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 900, color: COLORS.gray[900] }}>Deposit outcome (drop-off)</h4>
          <div style={{ marginTop: 10 }}>
            <span style={labelStyle}>Select one</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13, marginTop: 6 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" name="depositOutcome" checked={form.depositOutcome === 'full_release'} onChange={() => setDepositOutcome('full_release')} />
                Deposit fully released
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" name="depositOutcome" checked={form.depositOutcome === 'partial_release'} onChange={() => setDepositOutcome('partial_release')} />
                Partially returned
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" name="depositOutcome" checked={form.depositOutcome === 'on_hold'} onChange={() => setDepositOutcome('on_hold')} />
                Deposit kept on hold
              </label>
            </div>
          </div>

          {(form.depositOutcome === 'partial_release' || form.depositOutcome === 'on_hold') && (
            <label style={{ marginTop: 10, display: 'block' }}>
              <span style={labelStyle}>Reason</span>
              <textarea style={textareaStyle} value={form.depositOutcomeReason} onChange={(e) => updateField('depositOutcomeReason', e.target.value)} />
            </label>
          )}

          {(form.depositOutcome === 'full_release' || form.depositOutcome === 'partial_release') && (
            <>
              <div style={{ ...threeColResponsive, marginTop: 10 }}>
                <label>
                  <span style={labelStyle}>Release method</span>
                  <select style={inputStyle} value={form.depositReleaseMethod} onChange={(e) => updateField('depositReleaseMethod', e.target.value)}>
                    <option value="">Select…</option>
                    <option value="cash">Cash</option>
                    <option value="card_refund">Card refund</option>
                    <option value="hold_release">Card hold release</option>
                    <option value="other">Other</option>
                  </select>
                </label>

                {form.depositOutcome === 'partial_release' ? (
                  <label>
                    <span style={labelStyle}>Amount taken from deposit</span>
                    <input
                      type="number"
                      step="0.01"
                      style={inputStyle}
                      value={form.depositKeptAmount}
                      onChange={(e) => updateField('depositKeptAmount', e.target.value)}
                      placeholder="0.00"
                    />
                    <div style={{ fontSize: 11, color: COLORS.gray[500], marginTop: 2 }}>
                      Currency: <b>{depositCurrencyLabel}</b>
                    </div>
                  </label>
                ) : (
                  <label>
                    <span style={labelStyle}>Amount taken from deposit</span>
                    <input type="number" step="0.01" style={{ ...inputStyle, background: COLORS.gray[100] }} value="0.00" disabled />
                    <div style={{ fontSize: 11, color: COLORS.gray[500], marginTop: 2 }}>
                      Currency: <b>{depositCurrencyLabel}</b>
                    </div>
                  </label>
                )}

                <label>
                  <span style={labelStyle}>Amount released now (auto)</span>
                  <input
                    type="number"
                    step="0.01"
                    style={{ ...inputStyle, background: COLORS.gray[100] }}
                    value={form.depositToReturnAmount}
                    disabled
                  />
                  <div style={{ fontSize: 11, color: COLORS.gray[500], marginTop: 2 }}>
                    Currency: <b>{depositCurrencyLabel}</b>
                  </div>
                </label>
              </div>

              {form.depositOutcome === 'partial_release' && (
                <div style={{ fontSize: 12, color: COLORS.gray[500], marginTop: 8 }}>
                  Partial return: enter only the amount taken for charges; the released amount is computed automatically.
                </div>
              )}
            </>
          )}

          {form.depositOutcome === 'on_hold' && (
            <>
              <div style={{ ...threeColResponsive, marginTop: 10 }}>
                <label>
                  <span style={labelStyle}>Amount taken from deposit</span>
                  <input
                    type="number"
                    step="0.01"
                    style={inputStyle}
                    value={form.depositKeptAmount}
                    onChange={(e) => updateField('depositKeptAmount', e.target.value)}
                    placeholder="0.00"
                  />
                  <div style={{ fontSize: 11, color: COLORS.gray[500], marginTop: 2 }}>
                    Currency: <b>{depositCurrencyLabel}</b>
                  </div>
                </label>

                <label>
                  <span style={labelStyle}>Amount remaining on hold (auto)</span>
                  <input
                    type="number"
                    step="0.01"
                    style={{ ...inputStyle, background: COLORS.gray[100] }}
                    value={form.depositOnHoldAmount}
                    disabled
                  />
                  <div style={{ fontSize: 11, color: COLORS.gray[500], marginTop: 2 }}>
                    Currency: <b>{depositCurrencyLabel}</b>
                  </div>
                </label>

                <div />
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                On hold can still include charges taken from the deposit. The remaining balance stays on hold for later checks.
              </div>
            </>
          )}

          <hr style={{ border: 0, borderTop: `1px solid ${COLORS.gray[200]}`, margin: '14px 0' }} />

          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 900, color: COLORS.gray[900] }}>Payments received for charges (outside deposit)</h4>
          <div style={{ fontSize: 12, color: COLORS.gray[500], marginTop: 4 }}>
            Use multiple rows. If you pick “Deducted from deposit”, it becomes informational (no amount/currency).
          </div>

          <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
            {(Array.isArray(form.chargePayments) ? form.chargePayments : []).map((row, idx) => (
              <div key={idx} style={{ border: `1px solid ${COLORS.gray[200]}`, borderRadius: 12, padding: 10, background: '#fff' }}>
                <div style={{ ...threeColResponsive, alignItems: 'end' }}>
                  <label>
                    <span style={labelStyle}>Method</span>
                    <select style={inputStyle} value={row.method || 'cash'} onChange={(e) => updatePaymentRow(idx, { method: e.target.value })}>
                      <option value="cash">Cash</option>
                      <option value="card">Card</option>
                      <option value="deposit">Deducted from deposit</option>
                      <option value="bank">Bank transfer</option>
                      <option value="other">Other</option>
                    </select>
                  </label>

                  {row.method === 'deposit' ? (
                    <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
                      <div
                        style={{
                          fontSize: 12,
                          color: COLORS.gray[500],
                          padding: '10px 12px',
                          border: `1px dashed ${COLORS.gray[300]}`,
                          borderRadius: 10,
                          background: COLORS.gray[50],
                        }}
                      >
                        Covered by the amount taken from deposit above. No entry needed here.
                      </div>
                    </div>
                  ) : (
                    <>
                      <label>
                        <span style={labelStyle}>Amount</span>
                        <input type="number" step="0.01" style={inputStyle} value={row.amount || ''} onChange={(e) => updatePaymentRow(idx, { amount: e.target.value })} />
                      </label>

                      <label>
                        <span style={labelStyle}>Currency</span>
                        <select style={inputStyle} value={row.currency || defaultCurrency} onChange={(e) => updatePaymentRow(idx, { currency: e.target.value })}>
                          {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </label>
                    </>
                  )}
                </div>

                <div style={{ ...twoColResponsive, marginTop: 8, alignItems: 'end' }}>
                  <label>
                    <span style={labelStyle}>Note (optional)</span>
                    <input style={inputStyle} value={row.note || ''} onChange={(e) => updatePaymentRow(idx, { note: e.target.value })} placeholder="receipt # / last 4 digits / etc." />
                  </label>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => removePaymentRow(idx)}
                      style={{
                        border: `1px solid ${COLORS.gray[300]}`,
                        background: '#fff',
                        borderRadius: 10,
                        padding: '8px 10px',
                        cursor: 'pointer',
                        fontSize: 12,
                      }}
                    >
                      Remove
                    </button>
                    {idx === (form.chargePayments?.length || 0) - 1 && (
                      <button
                        type="button"
                        onClick={addPaymentRow}
                        style={{
                          border: `1px solid ${COLORS.gray[900]}`,
                          background: COLORS.gray[900],
                          color: '#fff',
                          borderRadius: 10,
                          padding: '8px 10px',
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        + Add payment row
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 12 }}>
            <span style={labelStyle}>Lessor signature</span>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="radio" checked readOnly />
              Haste LLC E-Signature Digital ID 55712233254412
            </label>
          </div>

          <hr style={{ border: 0, borderTop: `1px solid ${COLORS.gray[200]}`, margin: '14px 0' }} />

          {/* Lessee signature */}
          <section style={{ padding: 0, margin: 0 }}>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 900, color: COLORS.gray[900] }}>Lessee signature</h4>

            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 10 }}>
              <input type="checkbox" checked={!!form.customerRefusedToSign} onChange={(e) => updateField('customerRefusedToSign', e.target.checked)} />
              Customer refused to sign
            </label>

            {!form.customerRefusedToSign && (
              <div style={{ marginTop: 10 }}>
                <SignaturePad value={form.lesseeSignatureDataUrl || ''} onChange={(dataUrl) => updateField('lesseeSignatureDataUrl', dataUrl)} />
                <div style={{ fontSize: 11, color: COLORS.gray[500], marginTop: 6 }}>
                  Customer signature is required unless “refused to sign” is checked.
                </div>
              </div>
            )}
          </section>
        </section>

        {/* Contract */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Contract terms & conditions</h3>

          <button
            type="button"
            onClick={() => setShowContract((v) => !v)}
            style={{
              marginTop: 4,
              marginBottom: 6,
              fontSize: 12,
              borderRadius: 999,
              border: `1px solid ${COLORS.gray[300]}`,
              padding: '4px 10px',
              background: COLORS.gray[50],
              cursor: 'pointer',
            }}
          >
            {showContract ? 'Hide contract text ▲' : 'Show contract text ▼'}
          </button>

          {!showContract && (
            <p style={{ fontSize: 11, color: COLORS.gray[400], marginTop: 4 }}>
              Contract text is hidden to save space. Click to view the full {location === 'AL' ? 'Albanian' : 'Georgian'} return terms.
            </p>
          )}

          {showContract && (
            <p
              style={{
                fontSize: 12,
                color: COLORS.gray[600],
                whiteSpace: 'pre-wrap',
                borderRadius: 8,
                border: `1px solid ${COLORS.gray[200]}`,
                padding: 10,
                background: COLORS.gray[50],
                maxHeight: 260,
                overflowY: 'auto',
              }}
            >
              {contractText}
            </p>
          )}
        </section>
      </fieldset>

      {/* Submit bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
        {submitted && !submitting && (
          <div
            style={{
              flex: 1,
              padding: '10px 12px',
              borderRadius: 10,
              border: `1px solid ${COLORS.successLight}`,
              background: COLORS.successLight,
              color: COLORS.success,
              fontSize: 12,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <div>
              <div style={{ fontWeight: 700 }}>Return inspection saved.</div>
              <div style={{ marginTop: 2, opacity: 0.9 }}>Email will be sent shortly (if configured).</div>
            </div>
            <span style={{ fontWeight: 800, fontSize: 14 }}>✓</span>
          </div>
        )}

        {submitting && (
          <div style={{ flex: 1, maxWidth: 420 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: COLORS.gray[500] }}>
              <span>{submitDetail || 'Working…'}</span>
              <span>{Math.round(submitPct)}%</span>
            </div>

            <div style={{ height: 8, background: COLORS.gray[200], borderRadius: 999, overflow: 'hidden', marginTop: 6 }}>
              <div style={{ height: '100%', width: `${submitPct}%`, background: '#2563eb' }} />
            </div>
          </div>
        )}

        {!readOnly && !submitted && (
          <Button
            type="submit"
            disabled={submitting || pendingPhotoUploads > 0 || failedPhotoUploads > 0}
          >
            {submitting
              ? 'Submitting…'
              : pendingPhotoUploads > 0
              ? `Uploading photos (${pendingPhotoUploads})…`
              : failedPhotoUploads > 0
              ? 'Resolve failed uploads'
              : 'Save return inspection'}
          </Button>
        )}
      </div>
    </form>
  );
}
